import { useCallback, useRef } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useTelemetry } from '@order/Telemetry';

import { useCart } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Telemetry events for the bundle flow.
 */
export const useBundleTelemetry = () => {
  const params = useLocalSearchParams<BundleScreenParams>();
  const { track } = useTelemetry();
  const { cart } = useCart();

  // ─── Refs ────────────────────────────────────────────────────────────

  // NOTE: The bundle machine keeps the actions it started with, so the events it
  //       reports read the latest order through this ref.
  const cartRef = useRef(cart);

  cartRef.current = cart;

  // ─── Handlers ────────────────────────────────────────────────────────

  const trackView = useCallback(() => {
    track('bundle.view', { entryPoint: params?.referrer });
  }, [params?.referrer, track]);

  const trackSelectModalView = useCallback(
    (selectedCount: number) => {
      track('bundle.select_modal.view', { slot: selectedCount + 1, selectedCount });
    },
    [track],
  );

  const trackProductSelected = useCallback(
    (selectedProduct: SelectedProductParams) => {
      track('bundle.product.selected', selectedProduct);
    },
    [track],
  );

  const trackProductRemoved = useCallback(
    (removedProduct: RemovedProductParams) => {
      track('bundle.product.removed', removedProduct);
    },
    [track],
  );

  const trackProductUnavailableTapped = useCallback(
    (unavailableProduct: UnavailableProductParams) => {
      track('bundle.product.unavailable_tapped', unavailableProduct);
    },
    [track],
  );

  const trackAbandoned = useCallback(
    (bundle: BundleSelectionParams) => {
      track('bundle.abandoned', bundle);
    },
    [track],
  );

  const trackAddToBagPressed = useCallback(
    (bundle: AddToBagPressedParams) => {
      track('bundle.add_to_bag.pressed', bundle);
    },
    [track],
  );

  const trackAddedToBag = useCallback(
    (run: AddedToBagParams) => {
      const { addedCount, failedProductSlugs } = run;

      const orderId = cartRef.current?.id;

      if (failedProductSlugs.length === 0) {
        track('bundle.add_to_bag.succeeded', { addedCount, orderId });

        return;
      }

      track('bundle.add_to_bag.partially_succeeded', {
        addedCount,
        failedCount: failedProductSlugs.length,
        failedProductSlugs,
        orderId,
      });
    },
    [track],
  );

  const trackFailedToAddToBag = useCallback(
    (run: FailedToAddToBagParams) => {
      const { failedProductSlugs } = run;

      track('bundle.add_to_bag.failed', {
        failedCount: failedProductSlugs.length,
        failedProductSlugs,
      });
    },
    [track],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    trackView,
    trackSelectModalView,
    trackProductSelected,
    trackProductRemoved,
    trackProductUnavailableTapped,
    trackAbandoned,
    trackAddToBagPressed,
    trackAddedToBag,
    trackFailedToAddToBag,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type BundleScreenParams = {
  referrer?: string;
};

type SelectedProductParams = {
  productId: string;
  productSlug: string;
  productName: string;
  cost: number;
  selectedCount: number;
};

type RemovedProductParams = {
  productSlug: string;
  remainingCount: number;
};

type UnavailableProductParams = {
  productId: string;
  productSlug: string;
};

type BundleSelectionParams = {
  selectedCount: number;
  totalCost: number;
};

type AddToBagPressedParams = BundleSelectionParams & {
  outOfStockProductSlugs: readonly string[];
  outOfStockIngredientNames: readonly string[];
};

type AddedToBagParams = {
  addedCount: number;
  failedProductSlugs: readonly string[];
};

type FailedToAddToBagParams = {
  failedProductSlugs: readonly string[];
};
